<template lang="pug">
.filters(v-if="!loading")
  .filters-time__selector
    .filters-time__selector-label
      inline-svg.filters-time__selector-label--icon(
        :src="require(`../../../../../assets/icons/cleaning_dashboard/calendar.svg`)"
      )
      .filters-time__selector-label--text {{ timeFilter?.placeholder }}
    select-field.filters-time__selector--select(
      v-if="timeFilter?.options?.length > 0",
      :options="timeFilter.options",
      :showCancel="false",
      :initialValue="timeFilter.options[0].id",
      @change="updateDateType"
    )
    date-field.filters-time__selector--date(
      v-if="showDateFields",
      :value="dateFrom",
      :disable="dateFieldDisabled",
      :maxDate="todaysDate",
      @select-date="selectDateFrom"
    )
    date-field.filters-time__selector--date(
      v-if="showDateTo",
      :value="dateTo",
      :disable="dateFieldDisabled",
      :minDate="dateToMinDate",
      :maxDate="dateToMaxDate",
      @select-date="selectDateTo"
    )
  .filters-open__filters-button
    inline-svg(:src="require(`../../../../../assets/icons/cleaning_dashboard/open-filters.svg`)")
    .filters-open__filters-button--text(@click="openFilters()") {{ filtersButtonText }}
  .filters-open__filters(v-if="!hideFilters")
    .filters-open__filters-form(v-show="loadingFilters.length === 0")
      .filters-open__filters-form-row(v-for="(row, index) in filtersScheme", :key="`row${index}`")
        select-field.filters-open__filters-form-item(
          v-for="item in row",
          :key="`row${index}_${item.name}`",
          :multiple="getSelectMultiple(item.name)",
          :placeholder="getSelectLabel(item.name)",
          :fullOptionsPath="getOptionsPath(item.name)",
          :initialValue="filtersValues?.[item.name] ?? null",
          :optionsParams="getOptionsParams(item.name)",
          :watch="getWatchData(getField(item.name), filtersValues)",
          @mounted="addLoadingFilters(item.name)",
          @load="removeLoadingFilters(item.name)",
          @change="val => updateFilters(val, item.name)"
        )
      .filters-open__filters-form-buttons
        .filters-open__filters-form-buttons-item.accept(@click="submitFilters()") {{ filtersLocale.form_buttons.submit }}
        .filters-open__filters-form-buttons-item.reset(@click="resetFilters()") {{ filtersLocale.form_buttons.cancel }}
    .filters-open__filters-form-loader(v-show="loadingFilters.length > 0")
      q-spinner(color="primary", size="3em")
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import selectField from "../../../../shared/general_components/fields/selectField";
import dateField from "../../../../shared/general_components/fields/dateField";
import { getWatchData } from "@/services/useWatchParent";
import { format, parse } from "date-fns";
import { useStore } from "../../../../../store";

const store = useStore();

const props = defineProps({
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах. Пример: 1 */
  facilityId: { type: Number, default: -1 },
  /** Фильтр даты от. Пример: "2025-10-28T21:00:00.000Z" */
  dateFrom: { type: String, default: "" },
  /** Фильтр даты до. Пример: "2025-10-29T21:00:00.000Z" */
  dateTo: { type: String, default: "" },
  /** Тип дат за которые запрашиваются данные. Пример: "today" */
  dateType: { type: String, default: "today" },
  /** Начальные значения фильтров. Пример: { building_ids: [1, 2, 3] } */
  initialFilters: { type: Object, default: () => {} },
  /** Фильтр даты от при произвольном промежутке. Пример: "01.01.1970" */
  customDateFrom: { type: String, default: () => "" },
  /** Фильтр даты до при произвольном промежутке. Пример: "01.01.1970" */
  customDateTo: { type: String, default: () => "" },
});

const emit = defineEmits([
  /** Эмит для применения фильтров. Пример:
  {
    companie_ids: [1, 2 ,3],
    building_ids: [1, 2 ,3],
    employee_ids: [1, 2, 3],
  }
   */
  "submit",
  /** Эмит для обновление даты от при фильтрах по произвольной дате. Пример: "01.01.1970" */
  "updateCustomDateFrom",
  /** Эмит для обновление даты до при фильтрах по произвольной дате. Пример: "01.01.1970" */
  "updateCustomDateTo",
]);

const path = "/api/v3/cleaning/dashboard/filters";

const filters = ref({});
const filtersValues = ref(props.initialFilters);
const timeFilter = ref({});
const filtersScheme = ref([]);
const filtersLocale = ref({});
const loading = ref(false);
const showDateFields = ref(true);
const hideFilters = ref(true);
const loadingFilters = ref([]);

const todaysDate = computed(() => {
  const date = new Date();
  return getFormatedDate(date);
});

const dateType = computed(() => props.dateType);
const dateFrom = computed(() => {
  if (dateType.value === "custom") {
    return props.customDateFrom;
  }
  const date = new Date(props.dateFrom);
  const localDate = getFormatedDate(date);
  return localDate;
});
const dateTo = computed(() => {
  if (dateType.value === "custom") {
    return props.customDateTo;
  }
  const date = new Date(props.dateTo);
  const localDate = getFormatedDate(date);
  return localDate;
});

const dateFieldDisabled = computed(() => dateType.value !== "custom");

const dateToMinDate = computed(() => {
  if (dateFrom.value === "") {
    return "";
  }
  const date = getParsedDate(dateFrom.value);
  return getFormatedDate(date);
});

const dateToMaxDate = computed(() => {
  if (dateFrom.value === "") {
    const date = new Date();
    return getFormatedDate(date);
  }
  const date = getParsedDate(dateFrom.value);
  const dayOfDate = date.getDate();
  const monthOfDate = date.getMonth();
  const yearOfDate = date.getFullYear();
  // Получаем максимальное количество дней в текущем месяце
  let lastDayOfMonth;
  if (monthOfDate <= 10) {
    lastDayOfMonth = new Date(`${yearOfDate}-${monthOfDate + 2}-1`);
  } else {
    lastDayOfMonth = new Date(`${yearOfDate}-1-1`);
  }
  lastDayOfMonth.setDate(0);
  const maxDaysInMonth = lastDayOfMonth.getDate();
  const maxDate = new Date(date.setDate(dayOfDate + maxDaysInMonth));
  if (maxDate > new Date()) {
    return todaysDate.value;
  }
  return format(maxDate, "dd.MM.yyyy");
});

const showDateTo = computed(() => {
  return showDateFields.value && dateType.value !== "today" && dateType.value !== "yesterday";
});

const filtersButtonText = computed(() => {
  if (hideFilters.value) {
    return filtersLocale.value?.form_header ?? "";
  } else {
    return filtersLocale.value?.form_header_hide ?? "";
  }
});

const getFormatedDate = date => {
  return format(date, "dd.MM.yyyy");
};

const getParsedDate = date => {
  return parse(date, "dd.MM.yyyy", new Date());
};

const selectDateFrom = val => {
  emit("updateCustomDateFrom", val);
};

const selectDateTo = val => {
  emit("updateCustomDateTo", val);
};

const updateDateType = val => {
  emit("updateDateType", val);
};

const getSelectMultiple = name => {
  return filters.value.find(item => item.name === name)?.multiple;
};

const getSelectLabel = name => {
  return filters.value.find(item => item.name === name)?.label;
};

const getOptionsPath = name => {
  const optionsPath = filters.value.find(item => item.name === name)?.options_path;
  return store.state.paths[optionsPath];
};

const getOptionsParams = name => {
  return filters.value.find(item => item.name === name)?.options_params ?? {};
};

const getField = name => {
  return filters.value.find(item => item.name === name);
};

const resetFilters = () => {
  hideFilters.value = true;
  filtersValues.value = {};
  loadingFilters.value = [];
  submitFilters();
};

const getFiltersData = async () => {
  if (!loading.value) {
    try {
      loading.value = true;

      const params = {
        facility_id: props.facilityId,
      };

      const { data } = await backend.index(path, { params }, { encodeNestedData: true });
      timeFilter.value = data?.filters_data?.basic_filters?.find(item => item.name === "interval");
      filters.value = data?.filters_data?.extend_filters;
      filtersScheme.value = data?.filters_data?.scheme;
      filtersLocale.value = data?.header?.filters_form;

      emit("setTitle", data?.header?.current_facility);
    } catch (err) {
      await handleError(err, { notify: false });
    } finally {
      loading.value = false;
    }
  }
};

const openFilters = () => {
  hideFilters.value = !hideFilters.value;
};

const resetDateFields = async () => {
  showDateFields.value = false;
  await nextTick();
  showDateFields.value = true;
};

const addLoadingFilters = name => {
  loadingFilters.value.push(name);

  if (!filtersValues.value?.[name]) {
    filtersValues.value[name] = [];
  }
};

const removeLoadingFilters = name => {
  loadingFilters.value = loadingFilters.value.filter(item => item !== name);
};

const updateFilters = (val, name) => {
  const selectValues = val.map(item => item.value);
  filtersValues.value = { ...filtersValues.value, [name]: selectValues };
};

const submitFilters = () => {
  emit("submit", filtersValues.value);
  hideFilters.value = true;
};

onMounted(async () => {
  await getFiltersData();
});

defineExpose({ resetDateFields });
</script>

<style scoped lang="scss">
.filters {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  &-time__selector {
    display: flex;
    justify-content: space-between;
    padding: 0 16px;
    background-color: var(--cleaning-dashboard-filters-date);
    border: 1px solid var(--cleaning-dashboard-filters-date-border);
    border-radius: 16px;
    width: fit-content;
    height: 60px;
    align-items: center;
    gap: 8px;

    &-label {
      display: flex;
      gap: 8px;
      align-items: center;
      color: var(--cleaning-dashboard-text-color);
    }

    &--select {
      width: 220px;
      border-radius: 8px;
      height: 40px;
    }

    &--date {
      height: 40px;
      margin: 0;
      width: 220px;
      border-radius: 8px;
      background-color: var(--cleaning-dashboard-filters-date-field);
    }
  }

  &-open__filters {
    min-width: 100%;

    &-button {
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 600;
      color: #5b7aff;
      cursor: pointer;
    }

    &-form {
      background-color: var(--cleaning-dashboard-filters-background);
      border-radius: 16px;
      padding: 16px;

      &-loader {
        display: flex;
        justify-content: center;
      }

      &-row {
        display: flex;
        justify-content: space-between;
        gap: 16px;
      }

      &-item {
        width: 100%;
      }

      &-buttons {
        display: flex;
        gap: 12px;
        margin-top: 16px;

        &-item {
          padding: 8px 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          font-weight: 600;
          cursor: pointer;

          &.accept {
            background-color: #5b7aff;
            color: #fff;
          }

          &.reset {
            background-color: var(--dashboard-card-background);
            color: #5b7aff;
            border: 2px solid #5b7aff;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.filters-time__selector--select {
  .form-field.form-multiselect {
    margin: 0;
    background-color: var(--cleaning-dashboard-filters-date-select-background);
    min-height: 40px;
    height: 40px;
    border: 1px solid var(--cleaning-dashboard-filters-date-select-border);

    .multiselect__single {
      background-color: var(--cleaning-dashboard-filters-date-select-background);
      color: var(--cleaning-dashboard-filters-date-select-color);
      min-height: 20px;
      line-height: 20px;
      margin: 0;
    }

    .option__title {
      white-space: nowrap;
    }

    .multiselect__select {
      top: 2px;
    }

    .multiselect__tags {
      padding: 9px 40px 0 15px;
      min-height: 40px;
    }

    .multiselect__input {
      background-color: var(--cleaning-dashboard-filters-date-select-background);
      color: var(--cleaning-dashboard-filters-date-select-color);
    }

    .multiselect__content-wrapper .multiselect__content {
      background-color: var(--cleaning-dashboard-filters-date-select-background);
      color: var(--cleaning-dashboard-filters-date-select-color);
    }
  }
}

.filters-time__selector--date {
  label,
  .q-field__control {
    height: 40px;
    border-radius: 8px;
    border: 1px solid var(--cleaning-dashboard-filters-date-border);
  }

  .q-field__native {
    color: var(--cleaning-dashboard-filters-date-select-color);
  }

  .q-field__append .cancel-datetime-field {
    color: var(--cleaning-dashboard-filters-date-select-color);
    margin-right: 10px;
  }

  .date-input-class .q-field__prepend {
    height: 40px;
  }
}

.filters-open__filters-form-item {
  .form-field.form-multiselect {
    margin: 0;
  }
}
</style>
